// src/components/auth/ForgotPassword.jsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axiosConfig from '../../api/axiosConfig';
import { useTranslation } from 'react-i18next';

const Logo = ({ size = 40 }) => (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none">
        <path d="M50 5 L90 25 L90 65 Q90 85 50 97 Q10 85 10 65 L10 25 Z" fill="#1d4ed8" />
        <path d="M50 12 L84 30 L84 64 Q84 80 50 91 Q16 80 16 64 L16 30 Z" fill="#2563eb" />
        <text x="28" y="58" fontFamily="Arial" fontWeight="bold" fontSize="32" fill="white" fontStyle="italic">i</text>
        <text x="42" y="62" fontFamily="Arial" fontWeight="bold" fontSize="36" fill="white">P</text>
    </svg>
);

const ForgotPassword = () => {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim()) { setError('Email richiesta'); return; }
        setLoading(true);
        setError('');
        try {
            await axiosConfig.post('/auth/forgot-password', { email: email.trim().toLowerCase() });
            setSent(true);
        } catch {
            // Mostra sempre successo — evita user enumeration
            setSent(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 relative overflow-hidden">
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-3xl" />
            </div>

            <div className="relative max-w-md w-full mx-4">
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <Logo size={48} />
                        <div className="text-left">
                            <h1 className="text-2xl font-bold text-white">InfraPilot</h1>
                            <p className="text-blue-300 text-xs">Construction Site Management</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white/95 backdrop-blur rounded-2xl shadow-2xl p-8">
                    {sent ? (
                        <div className="text-center">
                            <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                <span className="text-3xl">📧</span>
                            </div>
                            <h2 className="text-xl font-bold text-gray-900 mb-2">Email inviata!</h2>
                            <p className="text-gray-500 text-sm mb-6">
                                Se l'email <strong>{email}</strong> è registrata, riceverai le istruzioni per il reset entro pochi minuti.
                            </p>
                            <p className="text-xs text-gray-400 mb-6">
                                Non hai ricevuto l'email? Controlla la cartella spam o riprova tra qualche minuto.
                            </p>
                            <Link to="/login"
                                className="block w-full text-center py-2.5 bg-blue-600 text-white rounded-xl font-semibold text-sm hover:bg-blue-700 transition">
                                Torna al login
                            </Link>
                        </div>
                    ) : (
                        <>
                            <h2 className="text-xl font-bold text-gray-900 mb-1">Password dimenticata?</h2>
                            <p className="text-gray-500 text-sm mb-6">
                                Inserisci la tua email e ti invieremo le istruzioni per reimpostare la password.
                            </p>

                            {error && (
                                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                                    ⚠️ {error}
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                                    <input type="email" value={email}
                                        onChange={e => { setEmail(e.target.value); setError(''); }}
                                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                                        placeholder="nome@azienda.com"
                                        autoComplete="email" autoFocus />
                                </div>

                                <button type="submit" disabled={loading}
                                    className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition disabled:opacity-50 text-sm">
                                    {loading ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                            Invio in corso...
                                        </span>
                                    ) : 'Invia istruzioni reset'}
                                </button>
                            </form>

                            <p className="mt-6 text-center text-sm text-gray-500">
                                Ricordi la password?{' '}
                                <Link to="/login" className="text-blue-600 hover:underline font-semibold">{t('auth.login')}</Link>
                            </p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;